/** Settings → Brand (C0): logo, colours and font of the documents (contrast checked by the API). */

import {
  type BrandIdentity,
  type FontFamilyEnum,
  getAgencyBrandCurrentRetrieveQueryKey,
  useAgencyBrandCurrentLogoCreate,
  useAgencyBrandCurrentLogoDestroy,
  useAgencyBrandCurrentPartialUpdate,
  useAgencyBrandCurrentRetrieve,
} from "@agencyos/api-client";
import { Button, GlassCard, SelectField, Skeleton, TextField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { type CSSProperties, type FormEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { SettingsLayout } from "../components/SettingsLayout";
import { fieldErrors, useCanEditSettings } from "../lib/settings";

type ColorKey = "primary_color" | "secondary_color" | "accent_color" | "text_color";
const COLORS: { key: ColorKey; label: string }[] = [
  { key: "primary_color", label: "brand.primary" },
  { key: "secondary_color", label: "brand.secondary" },
  { key: "accent_color", label: "brand.accent" },
  { key: "text_color", label: "brand.text" },
];
const FONTS: FontFamilyEnum[] = ["Arial", "Helvetica", "Inter", "Georgia", "Times New Roman"];
const HEX = /^#[0-9A-Fa-f]{6}$/;

function BrandForm({ brand }: { brand: BrandIdentity }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const canEdit = useCanEditSettings();
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: getAgencyBrandCurrentRetrieveQueryKey() });
  const save = useAgencyBrandCurrentPartialUpdate({ mutation: { onSuccess: refresh } });
  const upload = useAgencyBrandCurrentLogoCreate({ mutation: { onSuccess: refresh } });
  const remove = useAgencyBrandCurrentLogoDestroy({ mutation: { onSuccess: refresh } });
  const fileInput = useRef<HTMLInputElement>(null);
  const [colors, setColors] = useState<Record<ColorKey, string>>({
    primary_color: brand.primary_color ?? "#0047AB",
    secondary_color: brand.secondary_color ?? "#C0392B",
    accent_color: brand.accent_color ?? "#F59E0B",
    text_color: brand.text_color ?? "#1A1A2E",
  });
  const [font, setFont] = useState<FontFamilyEnum>(brand.font_family ?? "Arial");
  const errors = fieldErrors(save.error);
  const logoError = fieldErrors(upload.error).logo;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate({ data: { ...colors, font_family: font } });
  };

  // Live preview of the chosen colours: agency data, not design tokens (documents only).
  const preview = {
    "--doc-primary": HEX.test(colors.primary_color) ? colors.primary_color : undefined,
    "--doc-secondary": HEX.test(colors.secondary_color) ? colors.secondary_color : undefined,
    "--doc-accent": HEX.test(colors.accent_color) ? colors.accent_color : undefined,
    "--doc-text": HEX.test(colors.text_color) ? colors.text_color : undefined,
    fontFamily: `"${font}", Arial, sans-serif`,
  } as CSSProperties;

  return (
    <div className="grid">
      <GlassCard as="section" className="stack">
        <h2>{t("brand.logo")}</h2>
        <div className="logo-frame">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={t("brand.logoAlt")} />
          ) : (
            <p className="muted">{t("brand.noLogo")}</p>
          )}
        </div>
        <p className="ao-field__hint">{t("brand.logoHint")}</p>
        {logoError ? (
          <p role="alert" className="ao-field__error">
            {logoError}
          </p>
        ) : null}
        {canEdit ? (
          <div className="row">
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="visually-hidden"
              aria-label={t("brand.chooseLogo")}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload.mutate({ data: { logo: file } });
                e.target.value = "";
              }}
            />
            <Button
              variant="secondary"
              disabled={upload.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {upload.isPending ? t("common.loading") : t("brand.chooseLogo")}
            </Button>
            {brand.logo_url ? (
              <Button variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate()}>
                {t("brand.removeLogo")}
              </Button>
            ) : null}
          </div>
        ) : null}
      </GlassCard>

      <GlassCard as="section" className="stack">
        <h2>{t("brand.colors")}</h2>
        <form onSubmit={onSubmit} className="stack" noValidate>
          {COLORS.map(({ key, label }) => (
            <div key={key} className="color-row">
              <input
                type="color"
                aria-label={t(label)}
                value={HEX.test(colors[key]) ? colors[key] : "#000000"}
                disabled={!canEdit}
                onChange={(e) => setColors((c) => ({ ...c, [key]: e.target.value.toUpperCase() }))}
              />
              <TextField
                label={t(label)}
                value={colors[key]}
                maxLength={7}
                disabled={!canEdit}
                error={errors[key]}
                onChange={(e) => setColors((c) => ({ ...c, [key]: e.target.value }))}
              />
            </div>
          ))}
          <SelectField
            label={t("brand.font")}
            value={font}
            disabled={!canEdit}
            options={FONTS.map((f) => ({ value: f, label: f }))}
            onChange={(e) => setFont(e.target.value as FontFamilyEnum)}
          />
          {brand.palette ? (
            <p className="muted">{t("brand.contrast", { ratio: brand.palette.text_contrast })}</p>
          ) : null}
          {canEdit ? (
            <div className="row">
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? t("common.loading") : t("common.save")}
              </Button>
              <p role="status" className={save.isError ? "ko" : "ok"}>
                {save.isSuccess ? t("common.saved") : save.isError ? t("common.fixErrors") : ""}
              </p>
            </div>
          ) : null}
        </form>
      </GlassCard>

      <GlassCard as="section" className="stack">
        <h2>{t("brand.sample")}</h2>
        <div className="doc-sample" style={preview}>
          <p className="doc-sample__title">{t("brand.sampleTitle")}</p>
          <p className="doc-sample__slogan">{t("app.tagline")}</p>
          <p>
            <span className="doc-sample__chip">{t("brand.sampleButton")}</span>
          </p>
        </div>
      </GlassCard>
    </div>
  );
}

export function BrandPage() {
  const { t } = useTranslation();
  const brand = useAgencyBrandCurrentRetrieve();
  return (
    <SettingsLayout title={t("brand.title")} intro={t("brand.intro")}>
      {brand.isPending ? (
        <GlassCard aria-busy="true">
          <Skeleton />
        </GlassCard>
      ) : brand.data?.status === 200 ? (
        <BrandForm brand={brand.data.data} />
      ) : (
        <GlassCard role="alert">{t("common.error")}</GlassCard>
      )}
    </SettingsLayout>
  );
}
